import React, { FunctionComponent } from 'react';
import { SbBlokData, storyblokEditable } from '@storyblok/react';

import { BankingAppFooter, ContactBar, IconListType, Locale, MaterialIcon } from '@savebetter/components';

import { localeCookieDomain } from '../../../config';
import { deleteCookie, setCookie } from '../utils';

import { GroupedLinkProps } from './GroupedLinks.types';
import { LinkProps } from './Link';
import { RichText, RichTextProps } from './RichText';
import { Section } from './Section';

const PreferredLocaleCookieName = 'preferred_locale';
const RedirectTargetCookieName = 'redirect_target';

type StoryblokIconOptions = 'Facebook' | 'LinkedIn' | 'Twitter' | 'Instagram' | 'Youtube';

interface ContactBar extends SbBlokData {
  email: string;
  phoneNumber: string;
  workingHours: string;
}

interface SocialMediaLink {
  url: string;
  icon: StoryblokIconOptions[];
  target: string;
}

interface SocialBox extends SbBlokData {
  title: string;
  links: SocialMediaLink[];
}

interface Blok extends SbBlokData {
  title: string;
  contactBar: ContactBar[];
  socialBox: SocialBox[];
  links: LinkProps[];
  copyright: string;
  groupedLinks: GroupedLinkProps[];
  useGroupedLinks: boolean;
  text: RichTextProps;
}

interface FooterProps {
  blok: Blok;
  customFooterContent?: string;
}

const IconNameMap: Record<string, IconListType> = {
  Facebook: 'FacebookOutlinedIcon',
  LinkedIn: 'LinkedInIcon',
  Twitter: 'TwitterIcon',
  Instagram: 'InstagramIcon',
  Youtube: 'YouTubeIcon'
};

export const Footerv2: FunctionComponent<React.PropsWithChildren<FooterProps>> = ({ blok, customFooterContent }) => {
  const { contactBar = [], links, copyright, socialBox, text, groupedLinks, useGroupedLinks } = blok;
  if (typeof window !== 'undefined' && localStorage.getItem('mobileApp') === 'true') {
    return null;
  }
  const socialMediaLinks =
    socialBox[0] && socialBox[0].links
      ? socialBox[0].links.map(({ url, icon }) => ({
          href: url,
          content: <MaterialIcon name={IconNameMap[icon[0]]} color="text-black-60" />
        }))
      : [];

  const footerLinks = links?.map(({ url: href, text, target }) => {
    const link = href.includes('blob.core') ? `https://assets-us.raisin.com/en-us/${href.split('/')[4]}` : href;
    return {
      href: link,
      text,
      target
    };
  });

  const groupedFooterLinks = groupedLinks?.map(({ links, categoryTitle }) => {
    const mutatedLinks = links.map(({ url: href, text, target }) => {
      const link = href.includes('blob.core') ? `https://assets-us.raisin.com/en-us/${href.split('/')[4]}` : href;
      return {
        href: link,
        text,
        target
      };
    });
    return {
      categoryTitle,
      links: mutatedLinks
    };
  });

  // currentPlatform should always be en-us for this application
  const currentPlatform = ((typeof window !== 'undefined' && window.location.pathname.split('/')[1].toLowerCase()) ||
    'en-us') as Locale;

  const onSelectHandler = (locale: Locale) => {
    const expiresInDays = 365;
    const expires = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000);
    document.cookie = setCookie(PreferredLocaleCookieName, locale, {
      expires,
      domain: localeCookieDomain
    });
    deleteCookie(RedirectTargetCookieName);
  };

  return (
    <div {...storyblokEditable(blok)}>
      {contactBar.length > 0 && (
        <div className="bg-black-10">
          <Section className="py-0" width="md" blok={contactBar[0]} hasPaddingBottom={false} hasPaddingTop={false}>
            <ContactBar
              email={contactBar[0].email}
              phoneNumber={contactBar[0].phoneNumber}
              workingHours={contactBar[0].workingHours}
            />
          </Section>
        </div>
      )}
      <Section className="py-0" width="md" hasPaddingBottom={true} hasPaddingTop>
        <BankingAppFooter
          links={footerLinks}
          customFooterContent={customFooterContent}
          socialMediaLinks={socialMediaLinks}
          socialMediaText={socialBox[0] ? socialBox[0].title : ''}
          footerParagraph={<RichText content={text} className="text-xs" />}
          locale={currentPlatform}
          shouldShowCountryPicker={true}
          copyright={copyright}
          onCountrySelectHandler={onSelectHandler}
          groupedLinks={groupedFooterLinks}
          useGroupedLinks={useGroupedLinks}
        />
      </Section>
    </div>
  );
};
